import React from 'react';
import { FileText } from 'lucide-react';
import { PageHeader, PageHero, PageFooter } from '../components/PageLayout';
import useSEO from '../hooks/useSEO';

const TermsPage = () => {
  useSEO({
    title: 'CGU - Conditions Générales d\'Utilisation | DJ Nomad',
    description: 'Conditions Générales d\'Utilisation de DJ Nomad.',
    canonical: 'https://djnomad.app/terms',
    robots: 'noindex, follow'
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <PageHeader />

      {/* Hero */}
      <PageHero 
        icon={FileText}
        title="Conditions Générales d'Utilisation"
        subtitle="Dernière mise à jour : 17 décembre 2024"
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 flex-1">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <div className="prose max-w-none space-y-8 text-gray-600">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Objet</h2>
              <p className="leading-relaxed">
                Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation de l'application web DJ Nomad (ci-après "l'Application"), accessible à l'adresse djnomad.app et ses sous-domaines.
              </p>
              <p className="leading-relaxed">
                En accédant et en utilisant l'Application, vous acceptez sans réserve les présentes CGU. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser l'Application.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description du Service</h2>
              <p className="leading-relaxed">
                DJ Nomad est une Progressive Web App (PWA) permettant de :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Créer et gérer des événements musicaux</li>
                <li>Utiliser un dual-deck professionnel pour mixer de la musique</li>
                <li>Télécharger des événements pour une utilisation offline</li>
                <li>Gérer des playlists et bibliothèques musicales</li>
                <li>Partager des QR codes pour les invités</li>
                <li>Synchroniser automatiquement les données entre appareils</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Inscription et Compte Utilisateur</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.1 Création de compte</h3>
              <p className="leading-relaxed">
                L'utilisation de certaines fonctionnalités de l'Application nécessite la création d'un compte utilisateur. Vous vous engagez à fournir des informations exactes, complètes et à jour lors de votre inscription.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.2 Sécurité du compte</h3>
              <p className="leading-relaxed">
                Vous êtes responsable de la confidentialité de vos identifiants de connexion et de toutes les activités effectuées sous votre compte. En cas d'utilisation non autorisée de votre compte, vous devez nous en informer immédiatement.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.3 Âge minimum</h3>
              <p className="leading-relaxed">
                L'utilisation de l'Application est réservée aux personnes âgées de 13 ans minimum. Les mineurs de moins de 18 ans doivent obtenir le consentement de leurs parents ou tuteurs légaux.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Utilisation du Service</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">4.1 Licence d'utilisation</h3>
              <p className="leading-relaxed">
                DJ Nomad vous accorde une licence personnelle, non exclusive, non transférable et révocable pour utiliser l'Application conformément aux présentes CGU.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">4.2 Usage autorisé</h3>
              <p className="leading-relaxed">
                L'Application est destinée à un usage personnel ou professionnel légal. Vous vous engagez à :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Respecter les droits d'auteur sur les fichiers musicaux que vous uploadez</li>
                <li>Ne pas utiliser l'Application à des fins illégales ou frauduleuses</li>
                <li>Ne pas perturber ou tenter de perturber le fonctionnement de l'Application</li>
                <li>Ne pas collecter ou extraire des données de l'Application par des moyens automatisés</li>
                <li>Ne pas créer de comptes multiples pour contourner des restrictions</li>
              </ul>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">4.3 Mode offline</h3>
              <p className="leading-relaxed">
                Le mode offline permet de télécharger des événements pour une utilisation sans connexion Internet. Les fichiers téléchargés sont stockés localement sur votre appareil via les technologies PWA (IndexedDB, Service Workers). Vous êtes responsable de l'espace de stockage utilisé.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Contenu Utilisateur</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">5.1 Propriété</h3>
              <p className="leading-relaxed">
                Vous conservez tous les droits de propriété intellectuelle sur les fichiers musicaux et contenus que vous uploadez sur l'Application. Vous accordez à DJ Nomad une licence mondiale, non exclusive et gratuite pour héberger, stocker et afficher votre contenu dans le cadre de la fourniture du service.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">5.2 Responsabilité</h3>
              <p className="leading-relaxed">
                Vous êtes seul responsable du contenu que vous uploadez et vous garantissez disposer de tous les droits nécessaires (droits d'auteur, droits voisins, droits à l'image) pour utiliser et partager ce contenu.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">5.3 Modération</h3>
              <p className="leading-relaxed">
                DJ Nomad se réserve le droit de supprimer tout contenu qui violerait les présentes CGU, la législation en vigueur ou les droits de tiers, sans préavis et sans indemnité.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Disponibilité et Modifications du Service</h2>
              <p className="leading-relaxed">
                Nous nous efforçons de maintenir l'Application disponible 24/7, mais ne garantissons pas une disponibilité ininterrompue. Des interruptions peuvent survenir pour maintenance, mises à jour ou raisons techniques.
              </p>
              <p className="leading-relaxed">
                Nous nous réservons le droit de modifier, suspendre ou interrompre tout ou partie de l'Application à tout moment, avec ou sans préavis.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Tarification</h2>
              <p className="leading-relaxed">
                L'Application est actuellement proposée gratuitement. Nous nous réservons le droit d'introduire des fonctionnalités payantes ou un modèle d'abonnement à l'avenir, avec un préavis raisonnable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation de Responsabilité</h2>
              <p className="leading-relaxed">
                L'Application est fournie "en l'état" sans garantie d'aucune sorte. Dans les limites autorisées par la loi :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Nous ne garantissons pas que l'Application sera exempte d'erreurs, de virus ou d'autres composants nuisibles</li>
                <li>Nous ne sommes pas responsables des pertes de données, interruptions d'événements ou dommages indirects</li>
                <li>Notre responsabilité est limitée au montant que vous avez payé pour utiliser l'Application (actuellement 0€)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Résiliation</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">9.1 Résiliation par l'utilisateur</h3>
              <p className="leading-relaxed">
                Vous pouvez cesser d'utiliser l'Application et supprimer votre compte à tout moment depuis les paramètres de votre profil.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">9.2 Résiliation par DJ Nomad</h3>
              <p className="leading-relaxed">
                Nous pouvons suspendre ou résilier votre accès à l'Application en cas de violation des présentes CGU, sans préavis ni indemnité.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modifications des CGU</h2>
              <p className="leading-relaxed">
                Nous nous réservons le droit de modifier les présentes CGU à tout moment. Les modifications entrent en vigueur dès leur publication sur l'Application. Votre utilisation continue de l'Application après modification vaut acceptation des nouvelles CGU.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Loi Applicable et Juridiction</h2>
              <p className="leading-relaxed">
                Les présentes CGU sont régies par le droit français. Tout litige relatif à l'interprétation ou l'exécution des présentes sera soumis aux tribunaux compétents de Paris, France.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact</h2>
              <p className="leading-relaxed">
                Pour toute question concernant les présentes CGU, vous pouvez nous contacter :
              </p>
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mt-4 space-y-2">
                <p><strong className="text-gray-900">Email :</strong> <a href="mailto:legal@djnomad.app" className="text-violet-600 hover:underline">legal@djnomad.app</a></p>
                <p><strong className="text-gray-900">Formulaire de contact :</strong> <a href="/contact" className="text-violet-600 hover:underline">djnomad.app/contact</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default TermsPage;